import { useState } from "react";

import { createFileRoute } from "@tanstack/react-router";

import { X } from "lucide-react";

import {
  type InboundSubmission,
  statusUpdatableKind,
  type SubmissionStatus,
  useInbound,
  useInboundStats,
  useUpdateSubmissionStatus,
} from "../api/hooks";
import { durationShortLabel, sinceLabel } from "../lib/relativeTime";

import { InboundOrganisationPanel } from "./-InboundOrganisationPanel";

// Provenance is two machine values: the property the submission came from and
// the route it came in through. Both are stored as machine values so new
// websites and new intake routes (newsletter, referral links) appear here
// without a schema change; unknown values render verbatim rather than hiding
// the lead.
const submittedFromLabels: Record<string, string> = {
  "ecosystem-tracker-website": "Ecosystem Tracker",
  "folarity-website": "Folarity",
};

const submittedViaLabels: Record<string, string> = {
  "early-access-form": "Early access form",
  "contact-form": "Contact form",
  "newsletter-form": "Newsletter form",
};

function provenanceLabel(submission: InboundSubmission): string {
  const from =
    submittedFromLabels[submission.submittedFrom] ?? submission.submittedFrom;
  const via =
    submittedViaLabels[submission.submittedVia] ?? submission.submittedVia;
  return `${from} · ${via}`;
}

const pillarLabels: Record<string, string> = {
  plan: "Plan",
  monitor: "Monitor",
  engage: "Engage",
};

function initials(submission: InboundSubmission): string {
  return (
    `${submission.firstName.charAt(0)}${submission.lastName.charAt(0)}`.toUpperCase() ||
    "?"
  );
}

// The structured answers are the substance of an early-access application -
// its free-text details are optional - so those cards state them on one line.
// An enquiry has no structured answers at all: its message is the substance,
// so it gets no facts line rather than an empty one.
function factsLine(submission: InboundSubmission): string | null {
  if (submission.kind !== "early_access_lead") return null;

  // Every entry has to be able to be absent, including the sites one: built
  // as a bare template literal it would render "null sites" for a row without
  // one, which reads as data rather than as a gap.
  const parts = [
    submission.sites
      ? submission.sites === "Just one"
        ? "1 site"
        : `${submission.sites} sites`
      : null,
    submission.totalArea,
    submission.pillars.map((pillar) => pillarLabels[pillar] ?? pillar).join(", "),
  ].filter((part): part is string => Boolean(part));

  if (parts.length === 0) return null;

  const tools = [
    ...submission.currentTools,
    ...(submission.otherTools ? [submission.otherTools] : []),
  ];
  if (tools.length > 0) parts.push(`Using ${tools.join(", ")}`);

  return parts.join(" · ");
}

// An application is assessed, an enquiry is answered. The card says which so a
// reader knows what is being asked of them before reading the body.
const kindLabels: Record<InboundSubmission["kind"], string> = {
  early_access_lead: "Application",
  contact_message: "Enquiry",
};

function SubmissionCard({
  submission,
  onPickOrganisation,
}: {
  submission: InboundSubmission;
  onPickOrganisation: () => void;
}) {
  const updateStatus = useUpdateSubmissionStatus();
  const fullName = `${submission.firstName} ${submission.lastName}`;

  // The message is an enquiry's whole content and the details are an
  // application's optional extra, but both render as the body of the card.
  const body = submission.message ?? submission.details;
  const facts = factsLine(submission);

  // A kind this build does not know cannot be routed to a status endpoint, so
  // the actions are withheld rather than sent somewhere that 404s. The rest of
  // the card still renders: an unrecognised row should be visible and legible,
  // not hidden, matching how unrecognised provenance renders verbatim.
  const canUpdateStatus = statusUpdatableKind(submission.kind);

  const move = (status: SubmissionStatus) =>
    updateStatus.mutate({ id: submission.id, kind: submission.kind, status });

  return (
    <div className="lead-card">
      <span className="lead-avatar">{initials(submission)}</span>
      <div className="lead-body">
        <div className="lead-header-row">
          <span className="lead-name">{fullName}</span>
          {submission.organisation && (
            <span className="lead-org">{submission.organisation}</span>
          )}
          <span className="lead-meta">
            {kindLabels[submission.kind]} · {provenanceLabel(submission)} ·{" "}
            {sinceLabel(submission.submittedAt)}
          </span>
        </div>

        {body && <div className="lead-message">{body}</div>}
        {facts && <div className="lead-facts">{facts}</div>}

        <div className="lead-actions-row">
          {/* The organisation the enquiry turned out to be about, which is our
              record - not the free text in the header, which is whatever the
              sender typed. Named per submission so ten cards do not offer ten
              identically named buttons. */}
          <button
            type="button"
            className={
              submission.resolvedOrganisation
                ? "lead-org-button"
                : "lead-link-button"
            }
            // Each begins with the words on the button, so voice control
            // matches what is on screen, and each ends with whose card it is,
            // so ten of them are ten different names.
            aria-label={
              submission.resolvedOrganisation
                ? `${submission.resolvedOrganisation.name} - change the organisation for ${fullName}`
                : `Link organisation for ${fullName}`
            }
            onClick={onPickOrganisation}
          >
            {submission.resolvedOrganisation?.name ?? "Link organisation"}
          </button>

          {submission.status === "replied" && (
            <span className="badge badge-verified">Replied</span>
          )}
          {submission.status === "not_a_fit" && (
            <span className="badge badge-muted">Not a fit</span>
          )}
          {canUpdateStatus && submission.status !== "waiting" && (
            <button
              type="button"
              className="lead-link-button"
              // Named per submission: ten cards otherwise offer ten
              // identically named buttons, which a screen reader or voice
              // control cannot tell apart.
              aria-label={`Move ${fullName} back to waiting`}
              disabled={updateStatus.isPending}
              onClick={() => move("waiting")}
            >
              Move back to waiting
            </button>
          )}

          <span className="lead-actions-right">
            {canUpdateStatus && submission.status !== "replied" && (
              <button
                type="button"
                className="lead-action-button"
                aria-label={`Mark ${fullName} replied`}
                disabled={updateStatus.isPending}
                onClick={() => move("replied")}
              >
                Mark replied
              </button>
            )}
            {/* Percent-encoded: the address is submitted by the sender, and a
                quoted local part can smuggle ?bcc=/&body= into the compose
                window the operator opens. */}
            <a
              className="lead-email"
              href={`mailto:${encodeURIComponent(submission.email)}`}
            >
              {submission.email}
            </a>
            {canUpdateStatus && submission.status !== "not_a_fit" && (
              <button
                type="button"
                className="lead-dismiss-button"
                title="Not a fit"
                aria-label={`${fullName} is not a fit`}
                disabled={updateStatus.isPending}
                onClick={() => move("not_a_fit")}
              >
                <X size={15} />
              </button>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

const emptyMessages: Record<string, string> = {
  waiting: "No one is waiting on a reply.",
  replied: "Nothing has been replied to yet.",
  all: "Nothing yet. Applications and enquiries from both websites appear here as soon as someone submits a form.",
};

function Inbound() {
  const [status, setStatus] = useState<SubmissionStatus | undefined>("waiting");
  // Held here rather than in the card: the list sits in a .panel, which carries
  // a backdrop-filter and so becomes the containing block for anything fixed
  // inside it. A slide-over rendered from a card would be positioned against
  // the list box instead of the viewport, leaving the header and the filters
  // clickable behind a dialog that claims to be modal.
  const [picking, setPicking] = useState<InboundSubmission | null>(null);
  const {
    data,
    isPending,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInbound(status);
  const { data: stats, isError: statsFailed } = useInboundStats();

  const submissions = data?.pages.flatMap((page) => page.items) ?? [];

  const pills: {
    label: string;
    value: SubmissionStatus | undefined;
    count?: number;
  }[] = [
    { label: "Waiting", value: "waiting", count: stats?.waitingCount },
    { label: "Replied", value: "replied", count: stats?.repliedCount },
    { label: "All", value: undefined, count: stats?.totalCount },
  ];

  return (
    <>
      <div className="inbound-header">
        <div className="page-header">
          <div className="page-title">Inbound</div>
          <div className="page-subtitle">
            Early access applications and contact enquiries, newest first
          </div>
        </div>
        <div>
          {pills.map((pill) => (
            <button
              key={pill.label}
              type="button"
              className="tag-filter-button"
              aria-pressed={status === pill.value}
              onClick={() => setStatus(pill.value)}
            >
              {pill.count === undefined ? pill.label : `${pill.label} ${pill.count}`}
            </button>
          ))}
        </div>
      </div>

      <div className="stats-row stats-row-3">
        <div className="stat-card">
          <div className="stat-label">Waiting for a reply</div>
          <div className="stat-value">{stats ? stats.waitingCount : "-"}</div>
          <div className="stat-sub">
            {statsFailed ? "The numbers could not be loaded" : "These people asked"}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Median reply time</div>
          <div className="stat-value">
            {stats?.medianReplyTimeSeconds != null
              ? durationShortLabel(stats.medianReplyTimeSeconds)
              : "-"}
          </div>
          <div className="stat-sub">
            {/* A failed fetch must not read as a quiet inbox. */}
            {statsFailed
              ? "The numbers could not be loaded"
              : stats?.medianReplyTimeSeconds != null
                ? "Aim for the same day"
                : "No replies yet"}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Replied</div>
          <div className="stat-value">{stats ? stats.repliedCount : "-"}</div>
          <div className="stat-sub">
            {statsFailed
              ? "The numbers could not be loaded"
              : stats
                ? `Out of ${stats.totalCount} ${stats.totalCount === 1 ? "submission" : "submissions"}`
                : "Out of everything received"}
          </div>
        </div>
      </div>

      <div className="panel">
        {isPending && <div className="panel-title">Loading…</div>}

        {isError && (
          <div className="panel-title">Inbound could not be loaded.</div>
        )}

        {!isPending && !isError && submissions.length === 0 && (
          <div className="panel-title">{emptyMessages[status ?? "all"]}</div>
        )}

        {submissions.length > 0 && (
          <div className="lead-list">
            {submissions.map((submission) => (
              <SubmissionCard
                key={submission.id}
                submission={submission}
                onPickOrganisation={() => setPicking(submission)}
              />
            ))}
          </div>
        )}

        {hasNextPage && (
          <button
            type="button"
            className="tag-filter-button lead-load-more"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        )}
      </div>

      {picking && (
        <InboundOrganisationPanel
          submission={picking}
          onClose={() => setPicking(null)}
        />
      )}
    </>
  );
}

export const Route = createFileRoute("/inbound")({
  component: Inbound,
});
